import React, { useState } from 'react';
import axios from 'axios';
import { Package, X, Tag } from 'lucide-react';

function AddProductForm({ userId, categories, onSuccess }) {
  const [sku, setSku] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!sku.trim() || !title.trim() || price === '') {
      return alert('SKU, título y precio son requeridos');
    }

    setLoading(true);
    try {
      const payload = {
        sku: sku.trim(),
        title: title.trim(),
        description: description.trim(),
        price: Number(price),
        user_id: userId,
        category_ids: selectedCats.map(c => c.id)
      };

      const baseUrl = process.env.REACT_APP_API_URL || '';
      await axios.post(`${baseUrl}/api/create_product`, payload);
      
      alert('Producto creado exitosamente');
      setSku('');
      setTitle('');
      setDescription('');
      setPrice('');
      setSelectedCats([]);
      onSuccess();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Error al crear producto');
    } finally {
      setLoading(false);
    }
  }

  function toggleCategory(cat) {
    setSelectedCats(prev => 
      prev.find(c => c.id === cat.id)
        ? prev.filter(c => c.id !== cat.id)
        : [...prev, cat]
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 font-montserrat flex items-center gap-2">
          <Package className="w-5 h-5 text-[#B1336E]" />
          Nuevo Producto
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              SKU *
            </label>
            <input
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Ingresa el SKU"
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Precio *
            </label>
            <input
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Título *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nombre del producto"
            className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Descripción
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripción del producto"
            rows={3}
            className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Categorías
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm flex items-center justify-between"
            >
              <span className="text-slate-600">
                {selectedCats.length > 0 
                  ? `${selectedCats.length} categoría(s) seleccionada(s)`
                  : 'Selecciona categorías'}
              </span>
              <Tag className="w-4 h-4 text-slate-400" />
            </button>

            {showCategoryDropdown && (
              <div className="absolute z-10 mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {categories.map((cat) => (
                  <label
                    key={cat.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCats.some(c => c.id === cat.id)}
                      onChange={() => toggleCategory(cat)}
                      className="w-4 h-4 text-[#B1336E] border-slate-300 rounded focus:ring-[#B1336E]"
                    />
                    <span className="text-sm font-montserrat text-slate-700">
                      {cat.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {selectedCats.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedCats.map((cat) => (
                <span
                  key={cat.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#B1336E]/10 text-[#B1336E] rounded-full text-xs font-semibold font-montserrat"
                >
                  {cat.name}
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className="hover:bg-[#B1336E]/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
          >
            {loading ? 'Guardando...' : 'Guardar Producto'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProductForm;